import { type RefObject } from 'react'
import { Button } from '~/components/ui/button'
import { Spinner } from '~/components/ui/spinner'
import { cn } from '~/lib/utils'
import { MAX_FILE_MB } from './constants'

// 개정본 xlsx 업로드/파싱 트리거. 드래그&드롭·클릭 선택·교체·초기화와
// "비교하기" 실행 버튼을 담당한다(검증·diff 호출은 useRevisionDiff가 처리).
export function RevisionUploadForm({
    file,
    dragOver,
    loading,
    inputRef,
    onPick,
    setDragOver,
    onRun,
    onReset,
}: {
    file: File | null
    dragOver: boolean
    loading: boolean
    inputRef: RefObject<HTMLInputElement | null>
    onPick: (f: File | null) => void
    setDragOver: (v: boolean) => void
    onRun: () => void
    onReset: () => void
}) {
    return (
        <div>
            <label
                htmlFor='revision-file'
                onDragOver={(e) => {
                    e.preventDefault()
                    setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault()
                    setDragOver(false)
                    onPick(e.dataTransfer.files?.[0] ?? null)
                }}
                className={cn(
                    'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[var(--radius)] border-2 border-dashed px-6 py-8 text-center transition-colors',
                    dragOver
                        ? 'border-primary bg-accent'
                        : 'border-input bg-muted/40 hover:bg-muted'
                )}>
                <span className='text-foreground text-sm font-bold'>
                    {file
                        ? file.name
                        : '개정본 xlsx를 끌어다 놓거나 클릭해 선택하세요'}
                </span>
                <span className='text-muted-foreground text-xs'>
                    {file
                        ? `${(file.size / 1024).toFixed(0)} KB · 다시 클릭하면 교체`
                        : `.xlsx 형식(평가매뉴얼) · 1개만 · 최대 ${MAX_FILE_MB}MB`}
                </span>
                <input
                    ref={inputRef}
                    id='revision-file'
                    type='file'
                    accept='.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    className='hidden'
                    onChange={(e) => onPick(e.target.files?.[0] ?? null)}
                />
            </label>
            {/* 업로드 제약 안내(허용 형식·최대 1개·크기 제한) */}
            <p className='text-muted-foreground mt-2 text-xs'>
                허용 형식 .xlsx(평가매뉴얼 양식) · 한 번에 1개 파일 · 최대{' '}
                {MAX_FILE_MB}MB. 여러 파일을 끌어다 놓으면 첫 번째 파일만 사용합니다.
            </p>
            <div className='mt-3 flex items-center gap-2'>
                <Button size='sm' disabled={!file || loading} onClick={onRun}>
                    {loading ? (
                        <>
                            <Spinner size='sm' />
                            비교 중…
                        </>
                    ) : (
                        '비교하기'
                    )}
                </Button>
                {file ? (
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={loading}
                        onClick={onReset}>
                        초기화
                    </Button>
                ) : null}
            </div>
        </div>
    )
}
